import React, { useCallback, useContext, useState } from 'react';
import Layout from 'layouts/Admin';
import { useTranslation } from 'react-i18next';
import { Heading } from '@design-system/elements';
import { translations } from 'translations';
import { useDelete, useFetchAll, usePatch, usePost, usePostCsv } from './hooks';
import { WLContext } from '@contexts';
import { Button } from '@design-system/index';
import { Form } from './components/Form';
import { IContact, IContactPayload } from '@cincel/client';
import { useToggle } from 'hooks/useToggle';
import { Listing } from './components/Listing';
import { CsvForm } from './components/CsvForm';
import { Input } from '@design-system/inputs/BaseInputs';
import { useSession } from 'store/session';
import { cx } from '@emotion/css';

const DEFAULT_VALUES = {
  email: '',
  name: '',
};

export const ContactsPage = (): JSX.Element | null => {
  const { color } = useContext(WLContext);

  const {
    data,
    isLoading,
    setLike,
    page,
    setPage,
    pageSize,
    setPageSize,
    refetch,
  } = useFetchAll();

  const { accountLimits } = useSession();

  const { mutateAsync: onDelete } = useDelete();

  const { mutateAsync: onPatch } = usePatch();

  const { mutateAsync: onPost } = usePost();

  const { mutateAsync: onPostCsv } = usePostCsv();

  const { t } = useTranslation();

  const [action, setAction] = useState<'patch' | 'post' | 'csv' | null>(null);

  const [registry, setRegistry] = useState<IContactPayload>(DEFAULT_VALUES);

  const [isOpen, toggle] = useToggle(false);

  const prepareCsv = useCallback(() => {
    setAction('csv');
    toggle(true);
  }, []);

  const preparePatch = useCallback((r: IContact) => {
    setAction('patch');
    setRegistry(r);
    toggle(true);
  }, []);

  const preparePost = useCallback(() => {
    setAction('post');
    setRegistry(DEFAULT_VALUES);
    toggle(true);
  }, []);

  const handleOnDelete = useCallback(async (v: string) => {
    await onDelete(v);
    await refetch();
  }, []);

  const handleOnPatch = useCallback(async (v: IContactPayload) => {
    await onPatch(v);
    await refetch();
    toggle(false);
  }, []);

  const handleOnPost = useCallback(async (v: IContactPayload) => {
    await onPost(v);
    await refetch();
    toggle(false);
  }, []);

  const handleOnPostCsv = useCallback(async (v: { file: File | null }) => {
    if (v?.file) {
      await onPostCsv(v.file);
      await refetch();
      toggle(false);
    }
  }, []);

  if (isLoading) return null;

  return (
    <Layout className="px-4 md:px-0">
      <div className="mb-6 flex flex-col items-center justify-between gap-4 lg:flex-row">
        <div
          className={cx(
            accountLimits?.custom_branding_availability ? 'flex-[2]' : 'flex-1',
          )}>
          <Heading className="!mb-2">
            {t(translations.CONTACTS.HEADING)}
          </Heading>
          <p className="text-sm font-medium text-gray-300">
            {t(translations.CONTACTS.SUBHEADING)}
          </p>
        </div>

        {accountLimits?.custom_branding_availability && (
          <div className="flex flex-1 gap-4 lg:justify-end">
            <Button
              className="px-8 text-white"
              color={color}
              onClick={prepareCsv}
              size="sm"
              variant="outline">
              {t(translations.CONTACTS.UPLOAD_CSV)}
            </Button>
            <Button
              className="px-8 text-white"
              color={color}
              onClick={preparePost}
              size="sm"
              variant="solid">
              {t(translations.CONTACTS.BUTTON_ADD)}
            </Button>
          </div>
        )}
      </div>

      <Input
        className="mb-6 lg:w-[320px]"
        debounceInterval={500}
        onChange={setLike}
        placeholder={t(translations.COMMON.SERACH_BY_NAME)}
        primaryColor={color}
        size="sm"
      />

      <Listing
        data={data?.data}
        onDelete={handleOnDelete}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onUpdate={preparePatch}
        page={page}
        pageSize={pageSize}
        primaryColor={color}
        total={data?.count}
      />

      <Form
        action={action}
        defaultValues={registry}
        isOpen={isOpen && action !== 'csv'}
        onSubmit={action === 'patch' ? handleOnPatch : handleOnPost}
        onToggle={toggle}
      />

      <CsvForm
        isOpen={isOpen && action === 'csv'}
        onSubmit={handleOnPostCsv}
        onToggle={toggle}
      />
    </Layout>
  );
};

export default ContactsPage;
